import { useCallback, useEffect, useRef } from 'react';
import { useAccount, useConfig, useReconnect } from 'wagmi';

import { RECENT_CONNECTOR_KEY, RECONNECT_KEY } from '../../../constants';
import type { Chains, WalletAdapterData } from '../../../types';

import { useEagerConnect } from './use-eager-connect';

export const useAutoConnect = (wallets: WalletAdapterData[], chains: Chains, onComplete: () => void) => {
  const { storage } = useConfig();
  const { reconnectAsync } = useReconnect();
  const { isConnected } = useAccount();
  const { eagerConnect } = useEagerConnect(wallets, chains);

  const isReconnectingRef = useRef(false);

  const tryReconnect = useCallback(async () => {
    if (isConnected) {
      onComplete();
      return;
    }

    const [connectResult, recentConnector] = await Promise.all([
      eagerConnect(),
      storage?.getItem(RECENT_CONNECTOR_KEY),
    ]);
    if (connectResult || !recentConnector) {
      onComplete();
      return;
    }

    const lastConnector = await storage?.getItem(RECONNECT_KEY);
    const wallet = wallets.find((data) => data.walletId === lastConnector);

    if (!wallet) {
      onComplete();
      return;
    }

    await reconnectAsync({ connectors: [wallet.getConnector()] });
    onComplete();
  }, [isConnected, eagerConnect, storage, wallets, reconnectAsync, onComplete]);

  useEffect(() => {
    // StrictMode work-around
    if (isReconnectingRef.current) {
      return;
    }

    isReconnectingRef.current = true;

    tryReconnect();
  }, []);
};
